<template>
  <CreatorSetupTour :open="tourOpen" :steps="tourSteps" @close="closeTour" @finish="closeTour" @navigate="navigateFromTour" />
  <section class="creator-dashboard mx-auto max-w-[1700px] space-y-6 pb-12 text-slate-900">
    <header data-creator-tour="welcome" class="flex min-h-[188px] flex-col gap-5 lg:min-h-[164px] lg:flex-row lg:items-start lg:justify-between">
      <div class="min-w-0 flex-1">
        <p class="text-xs font-bold uppercase tracking-[0.22em] text-[#2D8CFF]">Your Platforma workspace</p>
        <h1 class="mt-2 min-h-[72px] text-3xl font-semibold tracking-tight text-slate-950 sm:min-h-[48px] sm:text-4xl">
          {{ isNewWorkspace ? 'Let’s build your member area' : 'Welcome back' }}<span v-if="creatorFirstName">, {{ creatorFirstName }}</span>.
        </h1>
        <p class="mt-2 min-h-[48px] max-w-2xl text-sm leading-6 text-slate-500">
          {{ isNewWorkspace ? 'Start with your space, create a product, and prepare your first paid membership.' : 'A clear view of your members, products, payments, and what needs your attention.' }}
        </p>
        <div class="mt-4 min-h-[38px]">
          <div v-if="siteUrl" class="inline-flex max-w-full items-center gap-2 rounded-full border border-white/90 bg-white/75 px-3 py-2 text-sm shadow-sm backdrop-blur-xl">
            <span class="shrink-0 font-medium text-slate-500">Your live site</span>
            <a :href="siteUrl" target="_blank" rel="noopener noreferrer" class="truncate font-semibold text-[#176fe2] hover:underline">{{ siteLabel }}</a>
            <button type="button" class="shrink-0 rounded-full p-1 text-slate-400 transition hover:bg-blue-50 hover:text-[#2D8CFF]" :aria-label="copied ? 'Link copied' : 'Copy live site link'" @click="copyDomain">
              <CheckIcon v-if="copied" class="h-4 w-4 text-emerald-600" />
              <ClipboardIcon v-else class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <div class="flex min-h-[42px] shrink-0 flex-wrap gap-2 self-start">
        <button v-if="!loading && !isStaffAccount" ref="tourTriggerRef" type="button" class="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-2.5 text-sm font-semibold text-[#176fe2] shadow-sm backdrop-blur-xl transition hover:bg-blue-100" @click="openTour">
          <SparklesIcon class="h-4 w-4" /> Take a tour
        </button>
        <button type="button" class="inline-flex items-center justify-center gap-2 rounded-xl border border-white bg-white/80 px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm backdrop-blur-xl transition hover:bg-white disabled:opacity-50" :disabled="loading" @click="loadDashboard">
          <ArrowPathIcon class="h-4 w-4" :class="loading ? 'animate-spin' : ''" />
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </header>

    <p v-if="errorMessage" role="status" class="rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800">{{ errorMessage }}</p>

    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <article v-for="card in metricCards" :key="card.label" class="dashboard-glass p-5">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-xs font-semibold uppercase tracking-[0.13em] text-slate-500">{{ card.label }}</p>
            <p class="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{{ card.value }}</p>
          </div>
          <span class="rounded-xl p-2.5" :class="card.iconClass"><component :is="card.icon" class="h-5 w-5" /></span>
        </div>
        <p class="mt-3 text-xs leading-5 text-slate-500">{{ card.detail }}</p>
      </article>
    </div>

    <div v-if="isNewWorkspace && firstIncompleteStep" data-creator-tour="next-step" class="dashboard-glass overflow-hidden p-0">
      <div class="flex flex-col gap-5 bg-gradient-to-r from-[#2D8CFF]/10 via-indigo-50/50 to-emerald-50/40 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">
        <div class="flex items-start gap-4">
          <span class="rounded-2xl bg-[#2D8CFF] p-3 text-white shadow-lg shadow-blue-500/15"><RocketLaunchIcon class="h-6 w-6" /></span>
          <div>
            <p class="text-xs font-bold uppercase tracking-[0.15em] text-[#176fe2]">Your next step</p>
            <h2 class="mt-1 text-xl font-semibold tracking-tight text-slate-950">{{ firstIncompleteStep.title }}</h2>
            <p class="mt-1 max-w-xl text-sm leading-6 text-slate-600">{{ firstIncompleteStep.description }}</p>
          </div>
        </div>
        <router-link :to="firstIncompleteStep.to" class="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#2D8CFF] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#176fe2]">
          {{ firstIncompleteStep.action }} <ArrowRightIcon class="h-4 w-4" />
        </router-link>
      </div>
    </div>

    <div class="grid gap-6 2xl:grid-cols-[minmax(0,1.6fr)_minmax(350px,.7fr)]">
      <div class="min-w-0"><CreatorRevenueChart class="dashboard-chart h-full" /></div>
      <CreatorMemberActivityStream class="dashboard-activity self-start overflow-hidden" />
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <article data-creator-tour="checklist" class="dashboard-glass p-5 sm:p-6">
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="flex items-center gap-2"><SparklesIcon class="h-5 w-5 text-[#2D8CFF]" /><h2 class="font-semibold text-slate-950">Launch checklist</h2></div>
            <p class="mt-1 text-sm text-slate-500">Build the foundation for your first paying members.</p>
          </div>
          <span class="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#176fe2]">{{ completedSteps }} of {{ setupSteps.length }}</span>
        </div>
        <div class="mt-5 h-2 overflow-hidden rounded-full bg-slate-100"><div class="h-full rounded-full bg-gradient-to-r from-[#2D8CFF] to-emerald-400 transition-all" :style="{ width: setupProgress + '%' }" /></div>
        <div class="mt-5 space-y-2">
          <router-link v-for="step in setupSteps" :key="step.id" :to="step.to" :data-creator-tour-step="step.id" class="flex items-center gap-3 rounded-xl border border-slate-100 bg-white/55 px-4 py-3 transition hover:border-blue-200 hover:bg-white">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" :class="step.done ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-[#2D8CFF]'">
              <CheckIcon v-if="step.done" class="h-4 w-4" /><span v-else class="text-xs font-bold">{{ step.number }}</span>
            </span>
            <span class="min-w-0 flex-1"><strong class="block text-sm font-semibold text-slate-800">{{ step.title }}</strong><span class="mt-0.5 block text-xs text-slate-500">{{ step.description }}</span></span>
            <ArrowRightIcon class="h-4 w-4 shrink-0 text-slate-400" />
          </router-link>
        </div>
      </article>

      <article data-creator-tour="membership-health" class="dashboard-glass p-5 sm:p-6">
        <div class="flex items-center gap-2"><ChartBarSquareIcon class="h-5 w-5 text-indigo-500" /><h2 class="font-semibold text-slate-950">Membership health</h2></div>
        <p class="mt-1 text-sm text-slate-500">The essentials for growing your member business.</p>
        <div class="mt-6 space-y-5">
          <div>
            <div class="flex justify-between gap-4 text-sm"><span class="text-slate-600">Member capacity</span><strong class="text-slate-900">{{ memberCapacityLabel }}</strong></div>
            <div v-if="memberLimit !== null" class="mt-2 h-2 overflow-hidden rounded-full bg-slate-100"><div class="h-full rounded-full bg-gradient-to-r from-[#2D8CFF] to-cyan-400" :style="{ width: memberCapacityPercent + '%' }" /></div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-xl border border-slate-100 bg-white/60 p-4"><p class="text-xs font-medium text-slate-500">Paid plans</p><p class="mt-1 text-xl font-semibold text-slate-900">{{ paidPlanCount === null ? '—' : formatCount(paidPlanCount) }}</p></div>
            <div class="rounded-xl border border-slate-100 bg-white/60 p-4"><p class="text-xs font-medium text-slate-500">Payments this month</p><p class="mt-1 text-xl font-semibold text-slate-900">{{ revenueSummary ? formatCount(revenueSummary.monthPaymentCount) : '—' }}</p></div>
          </div>
          <div class="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
            <p class="text-sm font-semibold text-slate-900">Ready to accept paid members?</p>
            <p class="mt-1 text-xs leading-5 text-slate-600">Create a paid membership plan and review your payment integration before inviting members.</p>
            <div class="mt-3 flex flex-wrap gap-3 text-xs font-semibold">
              <router-link to="/creator/app-settings/plan-tiers" class="text-[#176fe2] hover:underline">Membership plans →</router-link>
              <router-link to="/creator/app-settings/integrations" class="text-[#176fe2] hover:underline">Payment integrations →</router-link>
            </div>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowPathIcon, ArrowRightIcon, BanknotesIcon, ChartBarSquareIcon, CheckIcon, ClipboardIcon, CreditCardIcon, RocketLaunchIcon, SparklesIcon, Squares2X2Icon, UsersIcon } from '@heroicons/vue/24/outline';
import CreatorMemberActivityStream from '@/Components/CreatorMemberActivityStream.vue';
import CreatorRevenueChart from '@/Components/CreatorRevenueChart.vue';
import CreatorSetupTour from '@/Components/CreatorSetupTour.vue';
import { fetchCreatorStaffSession } from '@/utils/creatorStaffAccess';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';
const route = useRoute();
const router = useRouter();
const creatorFirstName = ref('');
const domain = ref('');
const customDomain = ref('');
const verified = ref(false);
const copied = ref(false);
const platformReady = ref(null);
const hasThemeBranding = ref(false);
const usageResources = ref(null);
const paidPlanCount = ref(null);
const revenueSummary = ref(null);
const loading = ref(true);
const errorMessage = ref('');
const tourOpen = ref(false);
const tourTriggerRef = ref(null);
const tourCreatorKey = ref('');
const isStaffAccount = ref(false);
const tourSessionVerified = ref(false);
let autoTourTimer = null;

const memberCount = computed(() => usageResources.value?.members?.usage ?? null);
const productCount = computed(() => usageResources.value?.product_features?.usage ?? null);
const memberLimit = computed(() => usageResources.value?.members?.publicLimit ?? null);
const siteLabel = computed(() => (verified.value && customDomain.value) || domain.value || '');
const siteUrl = computed(() => {
  const value = String(siteLabel.value || '').trim();
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : 'https://' + value;
});
const isNewWorkspace = computed(() => platformReady.value === false
  || (platformReady.value === true && Number(memberCount.value || 0) === 0 && Number(productCount.value || 0) === 0));
const setupSteps = computed(() => [
  { id: 'platform', number: 1, title: 'Initiate your member area', description: platformReady.value === true ? 'Your member area is ready. Continue building in Theme Editor.' : 'Create the space your members will call home.', done: platformReady.value === true, to: platformReady.value === true ? '/creator/theme-editor' : { path: '/creator/dashboard', query: { provisionPlatform: '1', continue: '/creator/theme-editor' } }, action: platformReady.value === true ? 'Open Theme Editor' : 'Initialize member area' },
  { id: 'brand', number: 2, title: 'Make it yours', description: 'Add your name, branding, and look in the Theme Editor.', done: hasThemeBranding.value, to: '/creator/theme-editor', action: 'Open Theme Editor' },
  { id: 'product', number: 3, title: 'Build your first product', description: 'Add a feature your members can use.', done: Number(productCount.value || 0) > 0, to: '/creator/theme-editor', action: 'Build a product' },
  { id: 'plan', number: 4, title: 'Set up a paid membership', description: 'Create a plan and choose how members will pay.', done: Number(paidPlanCount.value || 0) > 0, to: '/creator/app-settings/plan-tiers', action: 'Create a paid plan' },
]);
const completedSteps = computed(() => setupSteps.value.filter((step) => step.done).length);
const setupProgress = computed(() => Math.round((completedSteps.value / setupSteps.value.length) * 100));
const firstIncompleteStep = computed(() => setupSteps.value.find((step) => !step.done) || null);
const tourSteps = computed(() => [
  {
    id: 'welcome', title: 'Welcome to your workspace',
    description: 'This is your command center. Track your members, products, and payments here while you build your member experience.',
    targets: ['[data-creator-tour="welcome"]'],
  },
  ...setupSteps.value.map((step) => ({
    id: step.id,
    title: step.title,
    description: step.id === 'platform'
      ? (step.done ? 'Your member area is already active. Continue building it in Theme Editor.' : 'Start by opening your member area. Platforma prepares the space where your products and members will live.')
      : step.id === 'brand'
        ? 'The Theme Editor is where you give your member area its look, name, and branding.'
        : step.id === 'product'
          ? 'Add your first product feature so members have something valuable to experience.'
          : 'Create a paid membership plan, then connect a payment provider before inviting paying members.',
    done: step.done,
    to: step.to,
    action: step.action,
    targets: step.id === 'platform'
      ? ['[data-creator-tour="dashboard-nav"]', '[data-creator-tour="next-step"]', '[data-creator-tour-step="platform"]']
      : step.id === 'brand'
        ? ['[data-creator-tour="theme-editor"]', '[data-creator-tour-step="brand"]']
      : step.id === 'product'
          ? ['[data-creator-tour-step="product"]', '[data-creator-tour="checklist"]']
          : ['[data-creator-tour="membership-settings"]', '[data-creator-tour="membership-health"]', '[data-creator-tour-step="plan"]'],
  })),
]);
const memberCapacityPercent = computed(() => memberLimit.value ? Math.min(100, Math.round((Number(memberCount.value || 0) / Number(memberLimit.value)) * 100)) : 0);
const memberCapacityLabel = computed(() => {
  if (memberCount.value === null) return 'Loading…';
  return memberLimit.value === null ? formatCount(memberCount.value) + ' · Unlimited' : formatCount(memberCount.value) + ' of ' + formatCount(memberLimit.value);
});
const metricCards = computed(() => [
  { label: 'Revenue this month', value: revenueSummary.value ? formatMoney(revenueSummary.value.monthRevenueCents) : '—', detail: revenueSummary.value ? formatCount(revenueSummary.value.monthPaymentCount) + ' processed member payments' : 'Processed member payments', icon: BanknotesIcon, iconClass: 'bg-emerald-50 text-emerald-600' },
  { label: 'Your members', value: memberCount.value === null ? '—' : formatCount(memberCount.value), detail: 'Accounts in your member area', icon: UsersIcon, iconClass: 'bg-cyan-50 text-cyan-600' },
  { label: 'Product features', value: productCount.value === null ? '—' : formatCount(productCount.value), detail: 'Experiences built for your members', icon: Squares2X2Icon, iconClass: 'bg-indigo-50 text-indigo-600' },
  { label: 'Paid membership plans', value: paidPlanCount.value === null ? '—' : formatCount(paidPlanCount.value), detail: 'Active plans ready for enrollment', icon: CreditCardIcon, iconClass: 'bg-blue-50 text-[#2D8CFF]' },
]);

function formatCount(value) { return new Intl.NumberFormat('en-US').format(Math.max(0, Number(value || 0))); }
function formatMoney(cents) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(cents || 0) / 100); }
async function getJson(path) {
  const response = await fetch(apiProxyUrl + path, { credentials: 'include', cache: 'no-store' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Could not load dashboard data.');
  return payload;
}
async function loadDashboard() {
  if (autoTourTimer) { window.clearTimeout(autoTourTimer); autoTourTimer = null; }
  loading.value = true;
  errorMessage.value = '';
  const paths = [
    '/api/creator-details',
    '/api/creator-domain',
    '/api/creator-platform/status',
    '/api/app-settings',
    '/api/platforma-creator-billing/usage-limits',
    '/api/creator-payments/creator/plan-tiers?page=1&pageSize=100&scope=global&status=active',
    '/api/creator-payments/creator/revenue-summary?rangeKey=month',
  ];
  const dashboardRequests = paths.map(getJson);
  void dashboardRequests[0]
    .then((details) => {
      creatorFirstName.value = String(details?.firstName || details?.creatorFirstName || '').trim();
    })
    .catch(() => {});
  const results = await Promise.allSettled([...dashboardRequests, fetchCreatorStaffSession()]);
  const data = results.map((result) => result.status === 'fulfilled' ? result.value : null);
  if (data[0]) creatorFirstName.value = String(data[0].firstName || data[0].creatorFirstName || '').trim();
  if (data[1]) {
    domain.value = String(data[1].domain || '').trim();
    customDomain.value = String(data[1].customDomain || '').trim();
    verified.value = Boolean(data[1].verified);
  }
  if (data[2]) platformReady.value = Boolean(data[2].platform?.isProvisioned);
  if (data[3]) hasThemeBranding.value = Boolean(data[3].logo_url || data[3].favicon_url || data[3].membership_name);
  if (data[4]) usageResources.value = data[4].resources || null;
  if (data[5]) paidPlanCount.value = (Array.isArray(data[5].tiers) ? data[5].tiers : []).filter((tier) => Number(tier.priceCents || 0) > 0 && String(tier.status || '').toLowerCase() === 'active').length;
  if (data[6]) revenueSummary.value = data[6].summary || {};
  const session = data[7];
  isStaffAccount.value = Boolean(session?.isStaff || data[0]?.isStaff);
  tourSessionVerified.value = Boolean(session?.creator && !session?.isStaff);
  tourCreatorKey.value = String(session?.creator?.userId || session?.creator?.id || session?.creator?.creatorId || session?.creator?.email || '').trim().toLowerCase();
  if (results.slice(0, paths.length).every((result) => result.status === 'rejected')) errorMessage.value = 'Dashboard details are temporarily unavailable. Please refresh in a moment.';
  loading.value = false;
  maybeAutoOpenTour();
}
function tourStorageKey() { return tourCreatorKey.value ? `platforma:creator-setup-tour:v1:${tourCreatorKey.value}` : ''; }
function rememberTourSeen() {
  const key = tourStorageKey();
  if (!key) return;
  try { window.localStorage.setItem(key, 'seen'); } catch (_error) { /* The manual guide remains available if storage is blocked. */ }
}
function openTour() { rememberTourSeen(); tourOpen.value = true; }
function closeTour() {
  tourOpen.value = false;
  void nextTick(() => tourTriggerRef.value?.focus({ preventScroll: true }));
}
function maybeAutoOpenTour() {
  if (!tourSessionVerified.value || !tourCreatorKey.value || !isNewWorkspace.value || !firstIncompleteStep.value || route.query.provisionPlatform) return;
  if (platformReady.value === null || usageResources.value === null || paidPlanCount.value === null) return;
  try { if (window.localStorage.getItem(tourStorageKey())) return; } catch (_error) { return; }
  autoTourTimer = window.setTimeout(() => {
    if (route.path !== '/creator/dashboard' || tourOpen.value) return;
    openTour();
    autoTourTimer = null;
  }, 650);
}
function navigateFromTour(destination) {
  tourOpen.value = false;
  if (destination) void router.push(destination);
}
async function copyDomain() {
  if (!siteUrl.value) return;
  try {
    await navigator.clipboard.writeText(siteUrl.value);
    copied.value = true;
    window.setTimeout(() => { copied.value = false; }, 2000);
  } catch (_error) { copied.value = false; }
}
onMounted(loadDashboard);
onUnmounted(() => { if (autoTourTimer) window.clearTimeout(autoTourTimer); });
</script>

<style scoped>
.dashboard-glass {
  border: 1px solid rgba(255, 255, 255, .78);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, .66);
  box-shadow: 0 16px 48px rgba(30, 64, 115, .06), 0 1px 3px rgba(30, 64, 115, .04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}
.dashboard-chart,
.dashboard-activity {
  border-color: rgba(255, 255, 255, .78);
  background: rgba(255, 255, 255, .66);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

.dashboard-activity {
  height: clamp(560px, calc(100dvh - 210px), 620px);
  max-height: calc(100dvh - 180px);
}
</style>
